import { type ListingDataDescriptor, type ListingDataTranslationKey, type ListingFeatureDescriptor } from './types';

type ListingDataValueDescriptor = ListingDataDescriptor<string | number>;
type ListingDataRenderableDescriptor = ListingDataValueDescriptor | ListingFeatureDescriptor;

type DescriptorTranslationKey<TDescriptor extends ListingDataValueDescriptor> = Extract<
  TDescriptor['valueKey'] | TDescriptor['unitKey'],
  ListingDataTranslationKey
>;

/** A resolver may be scoped to the translation keys a specific descriptor can return. */
export type ListingDataTranslationResolver<TTranslationKey extends ListingDataTranslationKey> = (
  key: TTranslationKey,
  options?: { value: string | number },
) => string;

/**
 * Resolves the display string for a listing-data descriptor.
 *
 * An enabled feature resolves to its label. Other descriptors supply an already formatted direct
 * value or a translation key. A descriptor with both passes its formatted value to the translation
 * resolver as `options.value`. When they include a unit, the unit is appended after resolving a
 * translated unit key when necessary.
 */
export function resolveListingDataValue<TDescriptor extends ListingFeatureDescriptor>(
  descriptor: TDescriptor,
  translationResolver: ListingDataTranslationResolver<TDescriptor['labelKey']>,
): string;
export function resolveListingDataValue<TDescriptor extends ListingDataValueDescriptor>(
  descriptor: TDescriptor,
  translationResolver: ListingDataTranslationResolver<DescriptorTranslationKey<TDescriptor>>,
): string;
export function resolveListingDataValue(
  descriptor: ListingDataRenderableDescriptor,
  translationResolver: ListingDataTranslationResolver<ListingDataTranslationKey>,
): string {
  if (descriptor.value === true) {
    return translationResolver(descriptor.labelKey);
  }

  const value = descriptor.valueKey
    ? descriptor.value === undefined
      ? translationResolver(descriptor.valueKey)
      : translationResolver(descriptor.valueKey, { value: descriptor.value })
    : String(descriptor.value);
  const unit = descriptor.unitKey ? translationResolver(descriptor.unitKey) : descriptor.unit;

  return unit ? `${value} ${unit}` : value;
}
